import { EliteRule } from "../data/balance";
import { EventsData, TrialModifierData } from "../data/events";
import { Fnv1a64 } from "../maths/fnv1a64";
import { sameEventRule } from "../sim/eventRun";
import { LevelModifiers } from "../sim/modifiers";
import { STARS_PER_LEVEL, StarMask, starCount } from "../sim/stars";
import { SECONDS_PER_DAY, utcDay, utcWeek } from "./orders";

/**
 * The endgame's replays of cleared levels (FINISH_PLAN WP6, DESIGN §10.5): the Weekly Trial — one cleared level with
 * the week's modifier, rotated by ISO week — and the Heroic tier of a level with all three stars. Both are run
 * modifiers (sim/modifiers.ts), so a Trial or Heroic run replays, suspends and retries like any other run.
 */

const DAYS_PER_WEEK = 7;
/** orders.ts utcWeek's shift: day 0 (1970-01-01) was a Thursday, three days after a Monday. */
const WEEK_START_SHIFT_DAYS = 3;
/** Seed tag of the Trial's level pick, apart from every other hash of a week. */
const TRIAL_TAG = "weeklyTrial";

/** The UTC week (Monday to Sunday, as Daily Orders count it) of `now`. */
export function trialWeek(now: number): number { return utcWeek(utcDay(now)); }
/** When the week's Trial gives way to the next (Monday 00:00 UTC). */
export function trialEndsAt(week: number): number {
  // utcWeek(d) = floor((d + 3) / 7), so week w runs from day 7w − 3 and the next starts 7 days later.
  return ((week + 1) * DAYS_PER_WEEK - WEEK_START_SHIFT_DAYS) * SECONDS_PER_DAY;
}

/** The ISO 8601 week number (1–53) of a UTC day, for the Trial's label ("Week 44"). */
export function isoWeekNumber(day: number): number {
  const weekday = (((day + WEEK_START_SHIFT_DAYS) % DAYS_PER_WEEK) + DAYS_PER_WEEK) % DAYS_PER_WEEK;   // Monday 0
  const thursday = day - weekday + WEEK_START_SHIFT_DAYS;                                 // ISO: the week's Thursday names its year
  return Math.floor((thursday - yearStartDay(civilYear(thursday))) / DAYS_PER_WEEK) + 1;
}

/** The proleptic Gregorian year of a day number (days since 1970-01-01), by whole-number arithmetic. */
function civilYear(day: number): number {
  const z = day + 719468, era = Math.floor(z / 146097), doe = z - era * 146097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  return yoe + era * 400 + (mp >= 10 ? 1 : 0);
}
/** The day number of 1 January of `year`. */
function yearStartDay(year: number): number {
  const y = year - 1, era = Math.floor(y / 400), yoe = y - era * 400;
  const doy = Math.floor((153 * 10 + 2) / 5);                                         // 1 January, March-based month 10
  return era * 146097 + yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy - 719468;
}

/** The week's Trial: the featured level and its modifier. */
export interface WeeklyTrial { readonly week: number; readonly level: number; readonly modifier: TrialModifierData }

export function trialUnlocked(data: EventsData, highestCleared: number): boolean {
  return data.trial.modifiers.length > 0 && highestCleared >= data.trial.unlockAfterLevel;
}

/**
 * The Trial of `week` for a player whose highest cleared level is `highestCleared`: a level from `minLevel` up to it,
 * picked by a hash of the week, and the modifiers in turn. Pure and the same for everyone at the same progress;
 * TrialState locks it for the week at the player's first look, so clearing more levels mid-week does not move it.
 */
export function pickTrial(data: EventsData, week: number, highestCleared: number): WeeklyTrial | null {
  if (!trialUnlocked(data, highestCleared)) return null;
  const t = data.trial, count = highestCleared - t.minLevel + 1;
  const h = new Fnv1a64();
  h.writeString(TRIAL_TAG);
  h.writeInt(week);
  const level = t.minLevel + Number(h.value % BigInt(count));
  const n = t.modifiers.length;
  return { week, level, modifier: t.modifiers[((week % n) + n) % n] };
}

/** A Trial modifier as run modifiers: its elite rules replace the level's own. */
export function trialModifiers(m: TrialModifierData): LevelModifiers { return { enemyHpMult: m.enemyHpMult, elites: m.elites }; }

/** Heroic pressure: every raider's health × `enemyHpMult`; the level keeps its own elites. */
export function heroicModifiers(data: EventsData): LevelModifiers { return { enemyHpMult: data.heroic.enemyHpMult }; }

/** A level's Heroic tier opens once all its stars are earned. */
export function heroicOpen(stars: StarMask): boolean { return starCount(stars) >= STARS_PER_LEVEL; }

/** Marks the first Heroic win of `level` pays. */
export function heroicMarks(data: EventsData, level: number): number { return data.heroic.marksBase + data.heroic.marksPerLevel * level; }

const sameRules = (a: readonly EliteRule[] | undefined, b: readonly EliteRule[] | undefined): boolean =>
  a === undefined || b === undefined ? a === b : a.length === b.length && a.every((r, i) => r.affix === b[i].affix && r.every === b[i].every && r.fromStage === b[i].fromStage);

/** Whether two sets of run modifiers are the same run rules (a run's record against the Trial's or Heroic's). */
export function sameModifiers(a: LevelModifiers, b: LevelModifiers): boolean {
  const sameIron = (a.iron?.towers.join(",") ?? null) === (b.iron?.towers.join(",") ?? null);
  const sameDaily = (a.daily ? `${a.daily.day}:${a.daily.stages}` : null) === (b.daily ? `${b.daily.day}:${b.daily.stages}` : null);
  const sameChallenge = (a.challenge ? `${a.challenge.code}:${a.challenge.livesCap}` : null) === (b.challenge ? `${b.challenge.code}:${b.challenge.livesCap}` : null);
  return a.enemyHpMult.eq(b.enemyHpMult) && sameRules(a.elites, b.elites) && sameIron && sameDaily && sameChallenge && (a.endless?.through ?? null) === (b.endless?.through ?? null) && sameEventRule(a.event, b.event);
}

export interface TrialStateJson { week: number; level: number; modifier: string; wonWeek: number; heroic: number[] }

/** The locked Trial of the current week, the last week its reward was won, and the levels with a Heroic star. */
export class TrialState {
  week = -1;
  level = 0;
  modifierId = "";
  /** The last week whose Trial was won (its reward paid). */
  wonWeek = -1;
  private readonly heroicLevels: number[] = [];

  /**
   * This week's Trial: the one locked at the first look this week, or a fresh pick locked now. Null while the Trial is
   * locked for the player's progress. A clock set back to an earlier week keeps the locked Trial.
   */
  current(data: EventsData, now: number, highestCleared: number): WeeklyTrial | null {
    const week = trialWeek(now);
    if (week > this.week || this.locked(data) === null) {
      const t = pickTrial(data, week, highestCleared);
      if (t === null) return null;
      this.week = t.week; this.level = t.level; this.modifierId = t.modifier.id;
    }
    return this.locked(data);
  }

  /** The locked Trial as stored (no roll-over): what a run that just ended is measured against. */
  locked(data: EventsData): WeeklyTrial | null {
    const modifier = data.trial.modifiers.find((m) => m.id === this.modifierId);
    return this.week >= 0 && this.level >= 1 && modifier ? { week: this.week, level: this.level, modifier } : null;
  }

  wonThisWeek(): boolean { return this.wonWeek === this.week && this.week >= 0; }

  /** A win of the locked Trial: true the first time in its week (the reward pays then). */
  recordTrialWin(): boolean {
    if (this.week < 0 || this.wonWeek === this.week) return false;
    this.wonWeek = this.week;
    return true;
  }

  hasHeroic(level: number): boolean { return this.heroicLevels.includes(level); }
  get heroicCount(): number { return this.heroicLevels.length; }

  /** A Heroic win: true the first time on that level (its star lights and its Marks pay). */
  recordHeroicWin(level: number): boolean {
    if (!Number.isInteger(level) || level < 1 || this.hasHeroic(level)) return false;
    this.heroicLevels.push(level);
    this.heroicLevels.sort((a, b) => a - b);
    return true;
  }

  toJson(): TrialStateJson { return { week: this.week, level: this.level, modifier: this.modifierId, wonWeek: this.wonWeek, heroic: [...this.heroicLevels] }; }

  static fromJson(j: unknown): TrialState {
    const s = new TrialState();
    if (typeof j !== "object" || j === null) return s;
    const src = j as Partial<TrialStateJson>;
    if (Number.isInteger(src.week)) s.week = src.week!;
    if (Number.isInteger(src.level)) s.level = src.level!;
    if (typeof src.modifier === "string") s.modifierId = src.modifier;
    if (Number.isInteger(src.wonWeek)) s.wonWeek = src.wonWeek!;
    if (Array.isArray(src.heroic)) for (const l of src.heroic) if (Number.isInteger(l) && l >= 1) s.recordHeroicWin(l);
    return s;
  }
}
